import * as React from 'react';
import { analyticsTracker, classNames, ScriptLoader } from 'uitk-react-utils';
import { UitkImage } from 'uitk-react-images';
import { useLocalization } from '@shared-ui/localization-context';
import { UitkOverlayButton, UitkButtonSize } from 'uitk-react-button';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';
import styles from './uitk-video.css';
import { UitkLoader } from 'uitk-react-loader';
var PlayerStateMap = [['unstarted', -1], ['ended', 0], ['playing', 1], ['paused', 2], ['buffering', 3], ['cued', 5]];
var PlayerStates = new Map(PlayerStateMap);
var useVideoHook = function (analytics) {
  var _a = React.useState({
      status: false,
      data: 0
    }),
    playerError = _a[0],
    setPlayerError = _a[1];
  var _b = React.useState(false),
    isPlayerReady = _b[0],
    setIsPlayerReady = _b[1];
  var _c = React.useState(PlayerStates.get('unstarted')),
    playerState = _c[0],
    setPlayerState = _c[1];
  var _d = React.useState(),
    player = _d[0],
    setPlayer = _d[1];
  var onPlayerError = function (event) {
    setPlayerError({
      status: true,
      data: event.data
    });
  };
  var onPlayerReady = function (event) {
    setIsPlayerReady(true);
    setPlayerState(event.target.getPlayerState());
  };
  var onPlayerStateChange = function (event) {
    setPlayerState(event.data);
  };
  var onClickPlay = function () {
    if (typeof player !== 'undefined') {
      // @ts-ignore
      player.playVideo();
    }
    if (analytics) {
      analyticsTracker(analytics, 'playVideo', '');
    }
  };
  var onClickPause = function () {
    if (typeof player !== 'undefined') {
      // @ts-ignore
      player.pauseVideo();
    }
    if (analytics) {
      analyticsTracker(analytics, 'pauseVideo', '');
    }
  };
  return {
    playerError: playerError,
    onPlayerError: onPlayerError,
    isPlayerReady: isPlayerReady,
    onPlayerReady: onPlayerReady,
    playerState: playerState,
    onPlayerStateChange: onPlayerStateChange,
    player: player,
    setPlayer: setPlayer,
    onClickPlay: onClickPlay,
    onClickPause: onClickPause
  };
};

// Exclude this file from Jest due to testing issues on hooks. Will have to circle back on this.
var getClassNameList$2 = classNames.bind(styles);
var UitkVideo = function (_a) {
  var alt = _a.alt,
    analytics = _a.analytics,
    className = _a.className,
    isDialog = _a.isDialog,
    src = _a.src,
    buttonText = _a.buttonText;
  var _b = useVideoHook(analytics),
    playerError = _b.playerError,
    onPlayerError = _b.onPlayerError,
    isPlayerReady = _b.isPlayerReady,
    onPlayerReady = _b.onPlayerReady,
    playerState = _b.playerState,
    onPlayerStateChange = _b.onPlayerStateChange,
    player = _b.player,
    setPlayer = _b.setPlayer,
    onClickPlay = _b.onClickPlay,
    onClickPause = _b.onClickPause;
  var videoId = getVideoId(src);
  var _c = React.useState(videoId),
    playerVideoId = _c[0],
    setPlayerVideoId = _c[1];
  var wrapperVideoClassNames = getClassNameList$2('uitk-video', className);
  var updatePlayerVideo = function () {
    if (isPlayerReady) {
      setPlayerVideoId(videoId);
      // @ts-ignore
      player.cueVideoById(videoId);
    }
  };
  /* istanbul ignore next */
  var createPlayer = function () {
    window.YT.ready(function () {
      setPlayer(new window.YT.Player("player-".concat(videoId).concat(isDialog ? '-dialog' : ''), {
        events: {
          onError: onPlayerError,
          onReady: onPlayerReady,
          onStateChange: onPlayerStateChange
        },
        height: '100%',
        playerVars: {
          controls: 1,
          disablekb: 1,
          enablejsapi: 1,
          iv_load_policy: 3,
          modestbranding: 1,
          rel: 0
        },
        videoId: videoId,
        width: '100%'
      }));
    });
  };
  React.useEffect(function () {
    var mounted = true;
    if (!window.YT) {
      ScriptLoader.getScript("https://www.youtube.com/iframe_api").then(function () {
        if (mounted) {
          createPlayer();
        }
      }).catch(function (err) {
        console.error(err);
      });
    } else if (!player) {
      createPlayer();
    } else if (videoId !== playerVideoId) {
      updatePlayerVideo();
    }
    return function () {
      mounted = false;
    };
  });
  if (typeof document === 'undefined') {
    return null;
  }
  return React.createElement("div", {
    className: wrapperVideoClassNames
  }, React.createElement(UitkVideoLayer, {
    alt: alt || '',
    controls: {
      pause: onClickPause,
      play: onClickPlay
    },
    buttonText: buttonText,
    isPlayerReady: isPlayerReady,
    playerError: playerError,
    playerState: playerState,
    src: src
  }), React.createElement("div", {
    id: "player-".concat(videoId).concat(isDialog ? '-dialog' : '')
  }));
};

/**
 * Get the YouTube video Id from source Url.
 *
 * @param {string} src - The original YouTube Url.
 * @returns {string} - YouTube video Id.
 */
var getVideoId = function (src) {
  var _a;
  if (!src) {
    return '';
  }
  if (/v=/.test(src)) {
    var splitedString = src.split('v=');
    if (/&/.test(splitedString[1])) {
      return splitedString[1].split('&')[0];
    }
    return splitedString[1];
  }
  if (/youtu.be/.test(src)) {
    var splitedString = src.split('youtu.be/');
    return splitedString[1];
  }
  return ((_a = src.match(/[^/]+$/)) === null || _a === void 0 ? void 0 : _a[0]) || '';
};
/**
 * Check if url is valid YouTube thumbnail image.
 *
 * @param {string} src - The original YouTube Url.
 * @returns {boolean} - Returns boolean depending on image size.
 */
var checkImgUrl = function (url) {
  var img = new Image();
  img.src = url;
  return img.height > 90;
};
/**
 * Get max resolution thumbnail.
 *
 * @param {string} src - The original YouTube Url.
 * @returns {string} - Returns best image resolution thumbnail link is available.
 */
var youTubeThumbnailImage = function (src) {
  if (!src) {
    return '';
  }
  var imgResolutions = ['maxresdefault.jpg', 'hqdefault.jpg', 'sddefault.jpg', 'mqdefault.jpg', 'default.jpg'];
  var videoId = getVideoId(src);
  var youTubeThumbnailLink = 'https://i1.ytimg.com/vi';
  var thumbnailUrl = imgResolutions
  // eslint-disable-next-line array-callback-return
  .map(function (imgResolution) {
    var url = "".concat(youTubeThumbnailLink, "/").concat(videoId, "/").concat(imgResolution);
    if (checkImgUrl(url)) {
      return url;
    }
  }).filter(function (url) {
    return typeof url !== 'undefined';
  });
  return thumbnailUrl[0] || '';
};
/**
 * Check if provided src is a valid YouTube Url.
 *
 * @param {string} src - url
 * @returns {boolean}
 */
var isYoutubeSource = function (src) {
  if (!src) {
    return false;
  }
  return /(www\.)youtube\./.test(src) || /youtu\.be/.test(src);
};
var getClassNameList$1 = classNames.bind(styles);
var playerStateImageVisible = [PlayerStates.get('ended'), PlayerStates.get('unstarted'), PlayerStates.get('cued')];
var UitkVideoLayer = function (_a) {
  var alt = _a.alt,
    controls = _a.controls,
    isPlayerReady = _a.isPlayerReady,
    playerError = _a.playerError,
    playerState = _a.playerState,
    src = _a.src,
    buttonText = _a.buttonText;
  var coverSrc = youTubeThumbnailImage(src);
  var isPlaceholderImageVisibile = playerStateImageVisible.includes(playerState);
  return React.createElement(React.Fragment, null, isPlaceholderImageVisibile && React.createElement(UitkImage, {
    alt: alt,
    blankDefault: !coverSrc,
    src: coverSrc
  }), React.createElement(UitkVideoLayerLoading, {
    className: getClassNameList$1({
      'is-hidden': isPlayerReady
    }),
    playerState: playerState
  }), React.createElement(UitkVideoLayerControls, {
    className: getClassNameList$1({
      'is-hidden': !isPlayerReady || playerError.status,
      'uitk-image-placeholder uitk-image-placeholder-image': !coverSrc && ![PlayerStates.get('playing'), PlayerStates.get('paused')].includes(playerState)
    }),
    buttonText: buttonText,
    controls: controls,
    playerState: playerState
  }));
};
var getClassNameList = classNames.bind(styles);
var UitkVideoLayerControls = function (_a) {
  var className = _a.className,
    controls = _a.controls,
    playerState = _a.playerState,
    buttonText = _a.buttonText;
  var l10n = useLocalization();
  var playPauseControls = function () {
    return playerState === PlayerStates.get('playing') ? controls.pause : controls.play;
  };
  var playPauseText = function () {
    if (playerState === PlayerStates.get('playing')) {
      return (buttonText === null || buttonText === void 0 ? void 0 : buttonText.pauseButton) ? buttonText.pauseButton : l10n.formatText('video.pauseButton.label');
    }
    return (buttonText === null || buttonText === void 0 ? void 0 : buttonText.playButton) ? buttonText.playButton : l10n.formatText('video.playButton.label');
  };
  var playerStatesForIsVisuallyHidden = [PlayerStates.get('buffering'), PlayerStates.get('playing'), PlayerStates.get('unstarted')];
  return React.createElement("div", {
    className: getClassNameList(className)
  }, playerState === PlayerStates.get('paused') ? React.createElement(UitkScrim, {
    type: UitkScrimType.OVERLAY
  }) : null, React.createElement("button", {
    type: "button",
    "aria-label": playPauseText(),
    className: getClassNameList('uitk-video-layer-controls-link', {
      'is-visually-hidden': playerStatesForIsVisuallyHidden.includes(playerState)
    }),
    onClick: playPauseControls()
  }, React.createElement("span", {
    className: getClassNameList('is-visually-hidden')
  }, playPauseText())), React.createElement(UitkLayoutPosition, {
    type: "absolute",
    position: {
      top: 'center',
      left: 'center'
    },
    className: getClassNameList('uitk-video-play-button-wrapper')
  }, React.createElement(UitkOverlayButton, {
    "aria-hidden": "true",
    tabIndex: -1,
    onClick: playPauseControls(),
    size: UitkButtonSize.LARGE,
    className: getClassNameList({
      'is-visually-hidden': playerStatesForIsVisuallyHidden.includes(playerState)
    })
  }, React.createElement(UitkIcon, {
    id: "play",
    name: "play_arrow",
    size: UitkIconSize.LARGE,
    title: playPauseText()
  }))));
};
var UitkVideoLayerLoading = function (_a) {
  var className = _a.className,
    playerState = _a.playerState;
  var formatText = useLocalization().formatText;
  return React.createElement(React.Fragment, null, playerState === PlayerStates.get('unstarted') ? React.createElement(UitkScrim, {
    type: UitkScrimType.OVERLAY
  }) : null, React.createElement(UitkLayoutPosition, {
    className: className,
    type: "absolute",
    position: {
      top: 'center',
      left: 'center'
    }
  }, React.createElement(UitkLoader, {
    isDarkBackground: true,
    title: formatText('video.loading.title')
  })));
};
export { UitkVideo, UitkVideoLayer, UitkVideoLayerControls, UitkVideoLayerLoading, checkImgUrl, getVideoId, isYoutubeSource, youTubeThumbnailImage };